import { FC, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DropdownButton, DropdownOption } from '@dbt-labs/sourdough';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { MAX_PAGE_SIZE } from '@/hooks/discovery/apiConstants';
import { useCatalogGroups } from '@/hooks/discovery/catalog/groups';
import { useGroups } from '@/hooks/discovery/definitionGroups';
import { useLoadAllPaginatedResults } from '@/hooks/discovery/loadAllPaginatedResults';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';

export type GroupFilterDropdownProps = {};

export const GROUP_PARAM = 'group';

const GroupDropdown: FC<{
  groups: { name?: string | null }[] | undefined;
  paramName: string;
  label: string;
  className?: string;
  listClassName?: string;
}> = ({
  groups,
  paramName,
  label,
  className = 'text-nowrap text-fgMain',
  listClassName = 'z-50',
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const options = useMemo(
    () => [
      { label: 'All', value: 'all' },
      ...(groups?.map((group) => ({
        label: group.name || '',
        value: group.name || '',
      })) || []),
    ],
    [groups],
  );

  const defaultOption = useMemo((): DropdownOption => {
    const qsValue = searchParams.get(paramName);
    return qsValue
      ? options.find((o) => o.value === qsValue) || options[0]
      : options[0];
  }, [searchParams, options, paramName]);

  return (
    <DropdownButton
      options={options}
      name={label}
      className={className}
      listClassName={listClassName}
      defaultOption={defaultOption}
      trackingId="explorer-group-filter"
      onChange={({ value }) => {
        if (typeof value !== 'string') return;
        if (value === 'all') {
          setSearchParams((p) => {
            p.delete(paramName);
            return p;
          });
        } else {
          setSearchParams((p) => {
            p.set(paramName, value);
            return p;
          });
        }
      }}
    />
  );
};

export const GroupFilterDropdown: FC<GroupFilterDropdownProps> = () => {
  const { environmentId } = useDbtCloudProject();
  const { userId } = useEnvironmentScope();
  const groupsQuery = useGroups({
    environmentId,
    userId,
    filter: { uniqueIds: null },
    first: MAX_PAGE_SIZE,
  });

  const allGroupsQuery = useLoadAllPaginatedResults({
    paginatedQuery: groupsQuery,
    loadingStrategy: 'waitForAllResults',
    enabled: true,
  });

  return (
    <GroupDropdown
      groups={allGroupsQuery.data}
      paramName={GROUP_PARAM}
      label="Owner:"
    />
  );
};

export const AccountGroupFilterDropdown: FC = () => {
  const { accountId } = useDbtCloudAccount();
  const groupsQuery = useCatalogGroups({
    accountId,
    first: MAX_PAGE_SIZE,
    filter: { uniqueIds: null },
  });

  const allGroupsQuery = useLoadAllPaginatedResults({
    paginatedQuery: groupsQuery,
    loadingStrategy: 'waitForAllResults',
    enabled: true,
  });

  return (
    <GroupDropdown
      groups={allGroupsQuery.data}
      paramName={GROUP_PARAM}
      label="Owner:"
    />
  );
};
